import { Component, EventEmitter, HostBinding, Inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AbstractRuleFactory, PartialRule, RULE_FACTORY_SERVICE } from '@cbre/rule-engine';
import { Column, RuleAssociation } from '@miq/catalog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataObject } from '@shared/models/data-object.model';
import { combineLatest, map, Observable, share, switchMap } from 'rxjs';
import { ControlRulesResolver } from './control-rules.resolver';
import { RecordConfigFacts } from './controls-facts.model';
import { ControlsRuleEngineService } from './controls-rule-engine.service';

/**
 * This component builds a Engine agent if given Config (Column) has rules associated to it
 * and handles the engine output to apply the different behaviors rules describe
 */
@UntilDestroy()
@Component({
  selector: 'miqControlWithRuleEngine',
  template: `<ng-content></ng-content>`,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ControlWithRuleEngineComponent implements OnInit {
  @HostBinding('class.hidden') @Input() hidden = false;
  /**
   * root FormGroup control
   */
  @Input() formCtrl: UntypedFormGroup; // record

  /**
   * data control (control within formCtrl)
   */
  @Input() dataCtrl: AbstractControl; // data

  /**
   * Configuration data
   */
  @Input() config: Column = null;

  /**
   * Configuration data event emitter (to enable double data binding)
   */
  @Output() configChange = new EventEmitter<Column>();

  constructor(
    private controlRulesResolver: ControlRulesResolver,
    @Inject(RULE_FACTORY_SERVICE) private ruleFactoryService: AbstractRuleFactory<RecordConfigFacts>
  ) {}

  ngOnInit(): void {
    if (this.config.getRuleAssociations()?.length > 0) {
      // creates and set up an engin agent only if column has rules associated
      const ruleEngineAgent$ = this.buildAndConfigureEngine(this.config);
      // wire engine with handler
      ruleEngineAgent$.pipe(untilDestroyed(this)).subscribe(this._handleEngineOutput);
    }
  }

  /**
   * Handles and updates local instances based on Fact output and changes required to be applied
   * @param fact
   */
  private _handleEngineOutput = (fact: RecordConfigFacts): void => {
    const { config, data, record } = fact;
    // to make sure local control get's affected once outcomes are consumed
    let _shouldUpdateValueAndValidity = false;

    // handle if config comes out
    if (Object.entries(config ?? {}).some(([k, v]) => v !== this.config[k])) {
      this.config = Object.assign({}, this.config, config);
      _shouldUpdateValueAndValidity = this._toggleRequireValidator(this.config);
      this.configChange.emit(this.config);
    }

    // handle if data comes out
    if (data != null) {
      const parentCtrl = this.dataCtrl.parent;
      if (parentCtrl instanceof UntypedFormGroup) {
        this._updateControl(parentCtrl, data);
        _shouldUpdateValueAndValidity = true;
      }
    }

    // handle if record comes out
    if (record != null) {
      this._updateControl(this.formCtrl, record);
      _shouldUpdateValueAndValidity = true;
    }

    // queue control update value and validity (Apply changes on validators/data)
    if (_shouldUpdateValueAndValidity) {
      setTimeout(() => this.dataCtrl.updateValueAndValidity({ onlySelf: true }));
    }
  };

  /**
   * It add/removes Validators.required function from control and returns true
   * if validators list was affected
   * @param nextConfig
   * @returns
   */
  private _toggleRequireValidator(nextConfig: Column): boolean {
    if (nextConfig.required && !this.dataCtrl.hasValidator(Validators.required)) {
      this.dataCtrl.addValidators(Validators.required);
      return true;
    } else if (!nextConfig.required && this.dataCtrl.hasValidator(Validators.required)) {
      this.dataCtrl.removeValidators(Validators.required);
      return true;
    }
    return false;
  }

  /**
   * Updates a given control with given data using `onlySelf` update options as default
   * @param control
   * @param data
   * @param options
   * @returns
   */
  private _updateControl = (control: AbstractControl, data: DataObject, options: DataObject = { onlySelf: true }) =>
    setTimeout(() => control.patchValue(data, options));

  /**
   * It creates the engine agent (once) & shares the stream and then combines with form.valueChanges to produce
   * the two needed pieces to run and produce facts output (this is the most important part of this service).
   * @param config
   * @returns
   */
  private buildAndConfigureEngine(config: Column): Observable<Partial<RecordConfigFacts>> {
    // build engine
    const _engineBuilder = this._makeEngineBuilder(config);
    const engine$ = this.controlRulesResolver.loadRules<RecordConfigFacts>().pipe(map(_engineBuilder), share());

    // make facts stream
    const factsStream$ = this.formCtrl.valueChanges.pipe(map(this._toRecordConfigFacts));

    // combine, evaluate and return
    return combineLatest([engine$, factsStream$]).pipe(switchMap(([engine, facts]) => engine.evaluate(facts)));
  }

  /**
   * Returns a function that builds an Engine instance
   * @param config
   * @returns
   */
  private _makeEngineBuilder =
    <T>(config: Column) =>
    (rulesMap: { [key: string]: PartialRule<T> }) => {
      const _ruleBuilder = this._ruleInstanceBuilder(rulesMap);
      const rules = config.getRuleAssociations().map(_ruleBuilder);
      return new ControlsRuleEngineService({ rules });
    };

  /**
   * Transforms a RuleAssociation into a Rule instance
   * @param rulesMap
   * @returns
   */
  private _ruleInstanceBuilder = rulesMap => (ruleAssociation: RuleAssociation) => {
    const partialRuleDef = rulesMap[ruleAssociation.rule_id];
    return this.ruleFactoryService.getRuleInstance(partialRuleDef);
  };

  /**
   * Used in pipe/map of form.valueChanges to transform to RecordConfigFacts object defined
   * for controls module as the Fact object
   */
  private _toRecordConfigFacts = (record: DataObject): RecordConfigFacts => ({
    record,
    config: this.config,
    data: this.dataCtrl.parent?.value ?? record,
  });
}
